<template>
  <div>
    <v-data-table
      dense
      :headers="headers"
      :items="internalValue"
      :items-per-page="-1"
      hide-default-header
      hide-default-footer
      class="elevation-1 TrustDetails"
    >
      <!-- Each trust field carries its own floating label now, so the column
           headings are hidden - which leaves the toolbar (Forte / add row) with
           nowhere to live except the table's top slot. -->
      <template v-slot:top>
        <div class="d-flex align-center justify-end trust-toolbar">
          <FortePayment
            v-if="isLegacyTrustDeposit && !isPayrix && isVisibleForteBtn"
            @forteResponse="forteResponse"
          />
          <v-btn
            class="ml-2" color="primary"
            fab icon x-small
            @click="addOneAndFocusIfFirst"
            @keydown.enter.stop.prevent="addOneAndFocusIfFirst"
          >
            <v-icon>
              {{icons.mdiPlus}}
            </v-icon>
          </v-btn>
        </div>
      </template>

      <!-- Custom item slot to show extra fields below each row -->
      <template v-slot:item="props">
        <tr :class="['trust-item-first-row', props.index % 2 === 0 ? 'trust-item-even' : 'trust-item-odd']">
          <!-- Render all default columns using slot props -->
          <td class="trust-row-number">
            <span class="caption grey--text">{{ props.index + 1 }}.</span>
          </td>
          <td class="trust-col-tax-year">
            <div class="trust-inline-field">
              <span class="font-weight mr-2">Tax Year</span>
              <v-text-field
                :ref="`item_${props.index}`"
                dense
                hide-details
                v-model="props.item.taxYear"
                class="trust-tax-year input-text-size"
                single-line
                autocomplete="off"
                @blur="somethingChanged"
                @keydown="shortcutHandler($event, props.item)"
              ></v-text-field>
            </div>
          </td>
          <td class="trust-col-tax-id">
            <div class="trust-inline-field">
              <span class="font-weight mr-2">Tax ID</span>
              <v-text-field
                dense
                hide-details
                v-model="props.item.taxId"
                class="trust-tax-id input-text-size"
                single-line
                autocomplete="off"
                @blur="somethingChanged"
                @keydown="shortcutHandler($event, props.item)"
              ></v-text-field>
            </div>
          </td>
          <td class="trust-col-description">
            <div class="trust-inline-field">
              <span class="font-weight mr-2">Description</span>
              <v-textarea
                dense
                hide-details
                class="input-text-size"
                v-model="props.item.description"
                rows="1"
                autocomplete="off"
                @blur="somethingChanged"
                @keydown="shortcutHandler($event, props.item)"
              ></v-textarea>
            </div>
          </td>
          <td v-if="isLegacyTrustDeposit" class="trust-col-payment-type">
            <v-select
              dense
              hide-details
              class="input-text-size"
              label="Payment Type"
              :items="paymentTypes"
              item-text="description"
              item-value="name"
              v-model="props.item.paymentType"
              @blur="somethingChanged(); updateForteConfig()"
              @keydown="shortcutHandler($event, props.item)"
            ></v-select>
          </td>
          <td class="trust-col-amount">
            <div class="trust-inline-field">
              <span class="font-weight mr-2">Amount</span>
              <v-text-field
                dense
                hide-details
                v-model="props.item.amount"
                class="trust-amount input-text-size"
                single-line
                autocomplete="off"
                @blur="somethingChanged(); sumAmount(); updateForteConfig()"
                @keydown="shortcutHandler($event, props.item, true, props.index)"
                @keydown.enter="somethingChanged"
                @focus="focusAmount"
              ></v-text-field>
            </div>
          </td>
          <td class="trust-col-actions">
            <div class="d-flex align-center justify-end">
              <v-btn v-if="isLegacyTrustDeposit && isPayrix"
                class="input-text-size" dense
                color="primary" @click="handlePayrix(props.index)">
                Proceed to pay
              </v-btn>
              <v-icon
                medium class="od-trust-delete-icon"
                tabindex="-1"
                @click="removeOne(props.item)"
              >
                {{icons.mdiDelete}}
              </v-icon>
            </div>
          </td>
          <td v-if="isLegacyTrustDeposit" class="trust-col-paid">
            <v-checkbox
              small class="mr-2 od-trust-ispaid-checkbox input-text-size"
              hide-details
              label="Paid"
              v-model="props.item.isPaid"
              @change="somethingChanged"
            ></v-checkbox>
          </td>
        </tr>
        <!-- New row for VIN#, Title#, Decal -->
        <tr
          v-if="depositData && depositData.isPrePaidMobileHome"
          :class="['trust-extra-row', props.index % 2 === 0 ? 'trust-item-even' : 'trust-item-odd']"
        >
          <td :colspan="headers.length">
            <div class="d-flex flex-wrap align-center">
              <div class="mr-4 trust-extra-field">
                <span class="font-weight mr-2">VIN#</span>
                <v-text-field
                  dense
                  hide-details
                  class="input-text-size"
                  v-model="props.item.vinNumber"
                  single-line
                  autocomplete="off"
                  @blur="somethingChanged"
                ></v-text-field>
              </div>
              <div class="mr-4 trust-extra-field">
                <span class="font-weight mr-2">Title#</span>
                <v-text-field
                  dense
                  hide-details
                  class="input-text-size"
                  v-model="props.item.titleNumber"
                  single-line
                  autocomplete="off"
                  @blur="somethingChanged"
                ></v-text-field>
              </div>
              <div class="mr-4 trust-extra-field">
                <span class="font-weight mr-2">Decal</span>
                <v-text-field
                  dense
                  hide-details
                  class="input-text-size"
                  v-model="props.item.decal"
                  single-line
                  autocomplete="off"
                  @blur="somethingChanged"
                ></v-text-field>
              </div>
              <div class="mr-4 trust-extra-field">
                <span class="font-weight mr-2">Tag#</span>
                <v-text-field
                  dense
                  hide-details
                  class="input-text-size"
                  v-model="props.item.tag"
                  single-line
                  autocomplete="off"
                  @blur="somethingChanged"
                ></v-text-field>
              </div>
              <div class="mr-4 trust-extra-field">
                <span class="font-weight mr-2">Owner Name</span>
                <v-text-field
                  dense
                  hide-details
                  class="input-text-size"
                  v-model="props.item.ownerName"
                  single-line
                  autocomplete="off"
                  @blur="somethingChanged"
                ></v-text-field>
              </div>
              <div class="trust-extra-field trust-tax-district-field">
                <span class="font-weight mr-2">Tax District</span>
                <v-select
                  dense
                  hide-details
                  clearable
                  class="input-text-size"
                  :items="taxDistrictList"
                  item-text="taxDistrictCodeAndName"
                  item-value="districtCode"
                  v-model="props.item.taxDistrict"
                  single-line
                  @change="somethingChanged"
                ></v-select>
              </div>
            </div>
          </td>
        </tr>
        <!-- Stamp number range (MV Tax Stamps / Farm Stamps) -->
        <tr
          v-if="trustTypeRecord"
          :class="['trust-extra-row', props.index % 2 === 0 ? 'trust-item-even' : 'trust-item-odd']"
        >
          <td :colspan="headers.length">
            <div class="d-flex flex-wrap align-center">
              <div class="mr-4 trust-extra-field">
                <span class="font-weight mr-2">From #</span>
                <v-text-field
                  dense
                  hide-details
                  type="number"
                  v-model.number="props.item.fromNumber"
                  single-line
                  class="trust-from-number input-text-size"
                  @blur="somethingChanged"
                ></v-text-field>
              </div>
              <div class="mr-4 trust-extra-field">
                <span class="font-weight mr-2">To #</span>
                <v-text-field
                  dense
                  hide-details
                  type="number"
                  v-model.number="props.item.toNumber"
                  single-line
                  class="trust-to-number input-text-size"
                  @blur="somethingChanged"
                ></v-text-field>
              </div>
              <span class="caption grey--text">next available: {{ nextAvailableNumber }}</span>
            </div>
          </td>
        </tr>
      </template>

      <template slot="body.append" v-if="isLegacyTrustDeposit">
        <tr class="blue--text">
            <td colspan="2" id="total-trust-amount">
              Total : ${{ amountFormat(amountRemaining) }}
            </td>
        </tr>
        <tr class="blue--text" v-if="checkCount !== 0">
            <td colspan="2" id="total-check-count">
              Check Count : {{ checkCount }}
            </td>
        </tr>
      </template>

    </v-data-table>
    <PayrixPaymentDialog
    v-if="isDialogOpen && isLegacyTrustDeposit"
    :fieldNameLists="['Deposit Amount','Convenience fee (credit)','Convenience fee (debit)',]"
    />
  </div>
</template>

<script>
import {
  mapState,
  mapActions,
} from 'vuex';

import {
  mdiPlus,
  mdiDelete,
} from '@mdi/js';

import {
  isTabOrPlus,
  focusAmount,
  amountFormat,
  amountToNumber,
  isAltC,
  isAltR,
  isAltE,
} from '../../util/shared/vue-global';
import FortePayment from '../common/FortePayment.vue';
import PayrixPaymentDialog from '../common/PayrixPaymentDialog.vue';

const baseDefaultItem = {
  taxId: '',
  taxYear: '',
  description: '',
  amount: 0,
  vinNumber: '',
  titleNumber: '',
  decal: '',
  fromNumber: null,
  toNumber: null,
};

const legacyDefaultItem = {
  ...baseDefaultItem,
  paymentType: null,
};

// new-style trust deposits (isTrust flag): trustItems.amount is descriptive
// only (an itemized breakdown of the deposit for reference) - the real
// payment/reconciliation total still lives on the deposit's actual
// depositType (cashCheckItems/creditItems/EFTItems), same as any non-trust
// deposit
const newStyleDefaultItem = {
  ...baseDefaultItem,
};

export default {
  name: 'TrustDetails',
  components: {
    FortePayment,
    PayrixPaymentDialog,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    depositAmount: [Number, String],
    depositData: Object, // parent DepositDetails internalValue
    trustTypeRecord: { // selected Trust Type record (for stamp number tracking)
      type: Object,
      default: null,
    },
  },
  async created() {
    await this.loadConfig({ requiredScope: 'odconfig' });
    await this.loadConfig({ requiredScope: 'paymentConfig' });
  },
  data: () => ({
    icons: {
      mdiPlus,
      mdiDelete,
    },
    amountSum: 0.00,
    renderComponentFlag: true,
    taxDistrictsRequested: false,
  }),
  computed: {
    ...mapState({
      taxDistrictItems: (state) => state.base.crud.items.sharedTaxDistrict || [],
      paymentTypes: (state) => state.enums.ODTrustPaymentTypeEnum || [],
      forteConfig: (state) => state.ForteService.forteConfig,
      odSettingItem: (state) => state.SystemConfig.odSettingItem,
      paymentSettingItem: (state) => state.SystemConfig.paymentSettingItem || {},
      payrixSuccessFlag: (state) => state.common.payrixPayment.paymentSuccessFlag,
      isDialogOpen: (state) => state.common.payrixPayment.isDialogOpen,
    }),
    // A saved district that is no longer on the county list (or is still
    // loading) is kept as a bare code so the select shows it instead of
    // going blank.
    taxDistrictList() {
      const list = this.taxDistrictItems.map((t) => ({
        districtCode: t.districtCode,
        taxDistrictCodeAndName: `${t.districtCode} ${t.districtName || ''}`,
      }));
      const known = new Set(list.map((t) => t.districtCode));
      (this.internalValue || []).forEach((item) => {
        if (item.taxDistrict && !known.has(item.taxDistrict)) {
          known.add(item.taxDistrict);
          list.push({ districtCode: item.taxDistrict, taxDistrictCodeAndName: item.taxDistrict });
        }
      });
      return list;
    },
    // Trust deposits (isTrust flag, a real depositType) keep their money on
    // the sibling CashCheck/Credit/EFT component, so this component only shows
    // the descriptive columns for them. Otherwise (isTrust unchecked) the full
    // payment UI (paymentType/amount/isPaid per row) is shown.
    isLegacyTrustDeposit() {
      return !(this.depositData && this.depositData.isTrust);
    },
    headers() {
      const headers = [
        { text: '', value: 'rowNumber', sortable: false },
        { text: 'Tax Year', value: 'taxYear' },
        { text: 'Tax ID', value: 'taxId' },
        { text: 'Description', value: 'description' },
      ];
      if (this.isLegacyTrustDeposit) {
        headers.push({ text: 'Payment Type', value: 'paymentType' });
      }
      headers.push({ text: 'Amount', value: 'amount' });
      headers.push({ text: 'Actions', value: 'actions', sortable: false });
      if (this.isLegacyTrustDeposit) {
        headers.push({ text: 'Paid', value: 'paid', sortable: false });
      }
      // Do not add new columns here to avoid breaking layout
      return headers;
    },
    internalValue: {
      get() {
        const newVal = JSON.parse(JSON.stringify(this.value));
        const updatedValue = newVal.map(this.loadInitialValues);
        return updatedValue;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    amountValidation() {
      return [
        (value) => !!value || 'Required',
        (value) => value !== 0 || 'Should be non-zero',
      ];
    },
    totalCreditAmount() {
      let totalSum = 0.00;
      (this.internalValue || []).forEach((item) => {
        if (item.paymentType === 'CREDIT') {
          totalSum += amountToNumber(item.amount);
        }
      });
      return totalSum;
    },
    isVisibleForteBtn() {
      return this.renderComponentFlag && this.forteConfig.isConfigured;
    },
    amountRemaining() {
      let totalRemainingAmount = amountToNumber(this.depositAmount);
      for (let i = 0; i < (this.internalValue || []).length; i += 1) {
        totalRemainingAmount -= amountToNumber(this.internalValue[i].amount);
      }
      return totalRemainingAmount;
    },
    checkCount() {
      const paymentCheckCount = this.internalValue.filter((item) => item.paymentType === 'CHECK');
      return paymentCheckCount.length;
    },
    nextAvailableNumber() {
      const lastUsed = (this.trustTypeRecord && this.trustTypeRecord.lastNumberUsed) || 0;
      const highestToNumber = (this.internalValue || []).reduce(
        (max, item) => (item.toNumber > max ? item.toNumber : max), lastUsed,
      );
      return highestToNumber + 1;
    },
    isPayrix() {
      const settings = this.odSettingItem;
      const isPayrixGateway = settings.selectedGateway === 'payrix';
      const hasConfig = !!settings.payrixMerchentId && !!settings.payrixApiKey;
      const result = isPayrixGateway && hasConfig;
      return result;
    },
  },
  watch: {
    // the district list is only needed for the prepaid mobile home row
    'depositData.isPrePaidMobileHome': {
      immediate: true,
      handler(isPrePaidMobileHome) {
        if (isPrePaidMobileHome && !this.taxDistrictsRequested) {
          this.taxDistrictsRequested = true;
          this.loadTaxDistrict({ criteria: {}, baseCrudKey: 'sharedTaxDistrict' });
        }
      },
    },
    // isTrust being independent of depositType means CreditDetails/EFTDetails
    // and TrustDetails can now both be mounted at once (Payrix gateway, both
    // ccReconciliation/trustReconciliation on). Both watch this same shared
    // Vuex flag - without this guard, one successful payment would flip it
    // once and both components' watchers would fire, double-saving the
    // deposit. Only the component that actually owns the money for the
    // current depositType (legacy TRUST here) should react.
    payrixSuccessFlag: {
      immediate: false,
      handler(val) {
        if (val && this.isLegacyTrustDeposit) {
          this.$emit('payrixSuccess');
        }
      },
    },
  },
  mounted() {
    this.updateForteConfig();
  },
  methods: {
    ...mapActions('ForteService', [
      'fetchForteConfig',
    ]),
    ...mapActions('SystemConfig', [
      'loadConfig',
    ]),
    ...mapActions('shared/taxDistrict', [
      'loadTaxDistrict',
    ]),
    ...mapActions('common/payrixPayment', [
      'setPayrixPaymentData',
      'setPayrixPaymentType',
      'setPaymentContext',
      'setOdDepositData',
      'toggleDialog',
    ]),
    amountFormat,
    focusAmount,
    loadInitialValues(item) {
      return {
        ...item,
        amount: amountFormat(amountToNumber(item.amount)),
      };
    },
    shortcutHandler(e, item, isAddNew = false, index) {
      if (this.isLegacyTrustDeposit && (isAltC(e) || isAltR(e) || isAltE(e))) {
        this.changePaymentTypeByShortcut(e, item);
      }
      if (isTabOrPlus(e)) {
        if ((index + 1) === (this.internalValue || []).length) {
          if (this.isLegacyTrustDeposit) {
            this.sumAmount();
          }
          this.somethingChanged();
          if (this.isLegacyTrustDeposit
            && (amountToNumber(this.depositAmount) > this.sumAmount()) && isAddNew) {
            this.addByShortcut(e);
          }
        }
      }
    },
    addByShortcut(e) {
      e.preventDefault();
      this.addOneAndFocusIfFirst();
    },
    changePaymentTypeByShortcut(e, item) {
      e.preventDefault();
      const itemIndex = this.internalValue.indexOf(item);
      if (isAltC(e)) {
        const itemPaymentType = this.internalValue[itemIndex].paymentType;
        this.internalValue[itemIndex].paymentType = (itemPaymentType === 'CHECK') ? 'CASH' : 'CHECK';
      }
      if (isAltR(e)) {
        this.internalValue[itemIndex].paymentType = 'CREDIT';
      }
      if (isAltE(e)) {
        this.internalValue[itemIndex].paymentType = 'EFT';
      }
      this.somethingChanged();
    },
    addOneAndFocusIfFirst() {
      this.addOne();
      this.$nextTick(() => {
        const index = (this.internalValue || []).length;
        this.$refs[`item_${index - 1}`].focus();
      });
    },
    somethingChanged() {
      this.$emit('input', this.internalValue);
    },
    addOne() {
      const newItem = {
        ...(this.isLegacyTrustDeposit ? legacyDefaultItem : newStyleDefaultItem),
      };
      if (this.trustTypeRecord) {
        newItem.fromNumber = this.nextAvailableNumber;
      }
      this.$emit('input', [
        ...this.internalValue,
        newItem,
      ]);
      return true;
    },
    removeOne(item) {
      // const indexOfRemove = this.internalValue.indexOf(item);
      this.$emit('input', [
        ...this.internalValue.filter((candidate) => item !== candidate),
      ]);
    },
    sumAmount() {
      let totalSum = 0.00;
      for (let i = 0; i < (this.internalValue || []).length; i += 1) {
        totalSum += amountToNumber(this.internalValue[i].amount);
      }
      this.amountSum = totalSum;
      return totalSum;
    },
    forteResponse(response) {
      this.$emit('forteResponse', response);
    },
    async updateForteConfig() {
      if (!this.isLegacyTrustDeposit) {
        return;
      }
      // Remove forte-component from the DOM
      this.renderComponentFlag = false;
      // call forte event
      await this.fetchForteConfig({
        scope: 'odconfig',
        forteAmount: this.totalCreditAmount.toString(),
      });
      this.$nextTick(() => {
        // Add the forte-component back in
        this.renderComponentFlag = true;
      });
    },
    async handlePayrix(index) {
      const amt = amountToNumber(this.internalValue[index].amount);
      if (amt && amt > 0) {
        const creditFeePercentage = amountToNumber(this.paymentSettingItem.payrixCreditPercentage);
        const debitFeePercentage = amountToNumber(this.paymentSettingItem.payrixDebitPercentage);
        const debitFeeFlat = amountToNumber(this.paymentSettingItem.payrixDebitFeeFlat);
        const debitAmtLimit = amountToNumber(this.paymentSettingItem.payrixDebitAmtLimit);
        const convenienceFeeCredit = amountToNumber((amt * creditFeePercentage) / 100);
        const totalPlusCreditFee = amountToNumber(amt + convenienceFeeCredit);
        const eCheckFee = amountToNumber(this.paymentSettingItem.payrixEcheckFee);
        const totalPlusEcheckFee = amountToNumber(amt + eCheckFee);

        let convenienceFeeDebit = 0;

        if (debitAmtLimit > 0 && amt < debitAmtLimit) {
          convenienceFeeDebit = debitFeeFlat;
        } else {
          convenienceFeeDebit = amountToNumber((amt * debitFeePercentage) / 100);
        }
        const totalPlusDebitFee = amountToNumber(amt + convenienceFeeDebit);
        const payrixPaymentData = {
          totalAmount: amt,
          convenienceFeeCredit,
          totalPlusCreditFee,
          convenienceFeeDebit,
          totalPlusDebitFee,
          creditFeePercentage,
          debitFeePercentage,
          debitFeeFlat,
          eCheckFee,
          totalPlusEcheckFee,
        };
        await this.setPaymentContext('OD');
        await this.setOdDepositData(this.depositData);
        await this.setPayrixPaymentType('CREDIT');
        await this.setPayrixPaymentData(payrixPaymentData);
        await this.toggleDialog(true);
      } else {
        alert('Please enter a valid amount');
      }
    },
  },
};
</script>

<style lang="sass">
  .trust-amount input
    text-align: right

  .TrustDetails
    .trust-toolbar
      padding: 4px 4px 0 0

    .trust-row-number,
    .trust-col-actions,
    .trust-col-paid
      width: 1%
      white-space: nowrap

    .trust-col-payment-type
      width: 12%

    .trust-col-tax-year,
    .trust-col-tax-id,
    .trust-col-description,
    .trust-col-amount
      width: 21%

    .trust-inline-field
      display: flex
      align-items: center

      .font-weight
        font-size: 0.75rem
        white-space: nowrap
        flex: none

    .trust-extra-row td
      padding-top: 4px
      padding-bottom: 16px
      border-bottom: none

    .trust-extra-field.trust-tax-district-field
      width: 320px

    .trust-extra-field
      display: flex
      align-items: center
      width: 220px

      .font-weight
        font-size: 0.75rem
        white-space: nowrap
        flex: none

    td
      vertical-align: middle

    // Group a trust item's main row + its VIN/stamp sub-rows visually - a
    // thick top border marks where the next item starts, since the number
    // of sub-rows per item varies (prepaid-mobile-home / stamp-range rows
    // are conditional), so a border on the item's first row is the only
    // boundary that's always in the right place.
    .trust-item-first-row td
      border-top: 2px solid rgba(0, 0, 0, 0.24)
      border-bottom: none
      padding-top: 12px
      padding-bottom: 2px

    .trust-item-even
      background-color: rgba(0, 0, 0, 0.02)

    .trust-item-odd
      background-color: transparent
</style>
